// ---------------------------------------------------------------------------
// Label document -> ZPL II, for Zebra ZD230 and friends.
//
// Mirrors render/excelRenderer in the report builder: a pure function from the
// persisted document (plus data) to the output format. No React, no network.
//
// The printer renders the QR itself from ^BQ, so a job is a few hundred bytes of
// text rather than a bitmap — which is also what keeps the symbol crisp at 203 dpi.
// ---------------------------------------------------------------------------

import { DPMM_203, labelsPerPage, fontByKey } from "../labelSchema";
import { estimateTextWidthDots, wrapTextForFB } from "./labelGeometry";
import { rasterizeTextToGF } from "./textRaster";

export const mmToDots = (mm, dpmm = DPMM_203) => Math.round(mm * dpmm);

const ROTATIONS = { 0: "N", 90: "R", 180: "I", 270: "B" };
const rot = (deg) => ROTATIONS[deg] || "N";

const PLACEHOLDER_RE = /\{(\w+)\}/g;

/** Substitute {name} from the record; unknown keys are left visible, not blanked. */
export function interpolate(text, record) {
  if (!record) return String(text == null ? "" : text);
  return String(text == null ? "" : text).replace(PLACEHOLDER_RE, (whole, key) =>
    key in record ? String(record[key] == null ? "" : record[key]) : whole
  );
}

/** Every {placeholder} used in a string, in order of first appearance. */
export function placeholdersIn(text) {
  const out = [];
  const s = String(text == null ? "" : text);
  let m;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(s)) !== null) {
    if (out.indexOf(m[1]) === -1) out.push(m[1]);
  }
  return out;
}

/**
 * `^`, `~` and `\` steer the ZPL parser, so any of them inside field data has to be
 * hex-escaped with ^FH active. The ^FH prefix is emitted only when needed, to keep
 * generated ZPL readable in the common case.
 */
export function encodeField(value) {
  const s = String(value == null ? "" : value);
  if (!/[\^~\\]/.test(s)) return { prefix: "", data: s };
  const data = s.replace(
    /[\^~\\]/g,
    (c) => "\\" + c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")
  );
  return { prefix: "^FH\\", data };
}

function qrElement(el, dpmm, ox, oy, record) {
  const { prefix, data } = encodeField(interpolate(el.text, record));
  const x = ox + mmToDots(el.xMm, dpmm);
  const y = oy + mmToDots(el.yMm, dpmm);
  // ^FD format is <error correction level><input mode>,<data>; A = automatic.
  return `^FO${x},${y}^BQ${rot(el.rotation)},2,${el.magnification}${prefix}^FD${el.ecLevel}A,${data}^FS`;
}

function textElement(el, dpmm, ox, oy, record) {
  const resolved = interpolate(el.text, record);
  const x = ox + mmToDots(el.xMm, dpmm);
  const y = oy + mmToDots(el.yMm, dpmm);
  const block = mmToDots(el.widthMm, dpmm);

  // A system font is not in the printer, so it is drawn here and sent as a bitmap.
  // Wrapping has to happen on this side too, since ^FB only applies to ^FD text.
  if (el.fontSource === "system") {
    const { lines, overprint } = wrapTextForFB(resolved, block, el);
    const all = lines.concat(overprint.length ? [overprint.join(" ")] : []);
    const gf = rasterizeTextToGF(all.length ? all : [""], el, { blockDots: block });
    return `^FO${x},${y}${gf.zpl}^FS`;
  }

  const { prefix, data } = encodeField(resolved);
  const font = fontByKey(el.fontKey || "0");

  // ^A<font><orientation>,<height>,<width>. Width 0 lets a proportional font choose;
  // bitmap fonts need both, and only at multiples of their cell.
  const width = el.fontW ? `,${el.fontW}` : ",0";
  const fontCmd = `^A${font.key}${rot(el.rotation)},${el.fontH}${width}`;

  // ^FB gives the text box real meaning: it wraps and aligns within `block` dots.
  const field = `${fontCmd}^FB${block},${el.maxLines},0,${el.align}${prefix}^FD${data}^FS`;

  const parts = [`^FO${x},${y}${field}`];

  // No resident ZPL font has a bold weight, so bold is overprinting: the same field
  // drawn again one dot right and one dot down thickens every stroke. Two extra
  // passes is the most that still looks like type rather than a smudge.
  if (el.bold) {
    parts.push(`^FO${x + 1},${y}${field}`, `^FO${x},${y + 1}${field}`);
  }

  // Underline and strike-through are drawn as rules, since no resident font carries
  // them. Only meaningful on a single line: with ^FB the printer decides where the
  // breaks fall, so there is no reliable y for a rule under line 2.
  if ((el.underline || el.strikethrough) && (el.maxLines || 1) === 1) {
    const textW = Math.min(
      block,
      Math.ceil(estimateTextWidthDots(interpolate(el.text, record), el))
    );
    const lineX =
      el.align === "C"
        ? x + Math.round((block - textW) / 2)
        : el.align === "R"
        ? x + block - textW
        : x;
    const thickness = Math.max(1, Math.round(el.fontH / 12));
    if (el.underline) {
      parts.push(`^FO${lineX},${y + el.fontH + thickness}^GB${textW},0,${thickness}^FS`);
    }
    if (el.strikethrough) {
      parts.push(`^FO${lineX},${y + Math.round(el.fontH / 2)}^GB${textW},0,${thickness}^FS`);
    }
  }

  return parts.join("\n");
}

function renderElement(el, dpmm, ox, oy, record) {
  if (el.type === "qr") return qrElement(el, dpmm, ox, oy, record);
  if (el.type === "text") return textElement(el, dpmm, ox, oy, record);
  throw new Error("Unknown label element type: " + el.type);
}

/** Dot geometry of one sticker and of the whole printed page. */
export function pageGeometry(label) {
  const dpmm = label.dpmm || DPMM_203;
  const cellW = mmToDots(label.widthMm, dpmm);
  const cellH = mmToDots(label.heightMm, dpmm);
  const gapX = mmToDots(label.gapXMm || 0, dpmm);
  const gapY = mmToDots(label.gapYMm || 0, dpmm);
  const columns = label.columns || 1;
  const rows = label.rows || 1;
  return {
    dpmm,
    cellW,
    cellH,
    gapX,
    gapY,
    pageW: columns * cellW + (columns - 1) * gapX,
    pageH: rows * cellH + (rows - 1) * gapY,
  };
}

/**
 * One page of stickers.
 *
 * @param {object} label             the persisted label document
 * @param {object} [opts.records]    one record per cell, interpolated into element
 *   text. Omit for a page where every sticker is identical. A short array leaves the
 *   remaining cells blank rather than repeating data onto stickers nobody asked for.
 * @param {number} [opts.copies]     ^PQ page repeats
 * @returns {string} a complete ^XA…^XZ label format
 */
export function generateZpl(label, opts = {}) {
  const g = pageGeometry(label);
  const records = opts.records || null;
  const copies = Math.max(1, Math.round(opts.copies == null ? 1 : opts.copies));

  // Drawing commands only by default. ^PW, ^LL and ^MD all change settings the
  // printer retains after the job, and it already holds correct values for its own
  // media — so emitting them would push app state onto shared hardware.
  const out = ["^XA", "^CI28", "^LH0,0"];
  if (label.setPrintWidth) out.push(`^PW${g.pageW}`);
  if (label.setLabelLength) out.push(`^LL${g.pageH}`);
  if (label.setDarkness) out.push(`^MD${label.darkness}`);

  const offX = mmToDots(label.offsetXMm || 0, g.dpmm);
  const offY = mmToDots(label.offsetYMm || 0, g.dpmm);
  const columns = label.columns || 1;
  const rows = label.rows || 1;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < columns; c++) {
      const cell = r * columns + c;
      if (records && cell >= records.length) continue; // short last page
      const record = records ? records[cell] : null;
      const ox = offX + c * (g.cellW + g.gapX);
      const oy = offY + r * (g.cellH + g.gapY);
      (label.elements || []).forEach((el) => {
        out.push(renderElement(el, g.dpmm, ox, oy, record));
      });
    }
  }

  out.push(`^PQ${copies},0,0,N`);
  out.push("^XZ");
  return out.join("\n");
}

/** Split records into pages of the label grid. The last page may be short. */
export function paginate(records, perPage) {
  if (perPage < 1) throw new Error("perPage must be at least 1");
  const pages = [];
  for (let i = 0; i < records.length; i += perPage) pages.push(records.slice(i, i + perPage));
  return pages;
}

/**
 * A whole production run: records are paginated across the grid and each page is
 * emitted as its own format. ^PQ stays 1 — every sticker carries different data, so
 * there is nothing to repeat.
 */
export function generateBatchZpl(label, records) {
  return paginate(records, labelsPerPage(label))
    .map((page) => generateZpl(label, { records: page, copies: 1 }))
    .join("\n");
}
